import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { PropertiesResponseContract, Property, PropertyForInvitesResponseContract } from '../types/property';
import { UrlBuilder } from '../../../shared/utils/url-utils';
import { HttpGatewayService } from '../../../core/services/http-gateway.service';
import { CityData } from '../types/city-data';
import { PmsSetup } from '../types/pms';
import { Organization } from '../../../shared/types/organization';
import { SignedInUser } from '../../../shared/types/user';

const PROPERTY_URLS = {
    GET_PROPERTIES: 'api/v5/organization/{organizationId}/manage-properties/get-properties',
    CONFIGURE: 'api/v5/organization/{organizationId}/pms-connect/configure/{hotelCode}',
    CONFIGURE_PMS: 'api/v5/organization/{organizationId}/pms-connect/configure-pms/{hotelCode}',
    ADD_PROPERTY: 'api/v5/organization/{organizationId}/manage-properties/add-property',
    DISCONNECT_PROPERTY: 'api/v5/organization/{organizationId}/manage-properties/disconnect-property/{hotelCode}',
    UPDATE_HOTEL_CAPACITY: 'api/v5/organization/{organizationId}/manage-properties/update-hotel-capacity/{hotelCode}',
    UPDATE_HOTEL_DETAILS: 'api/v5/organization/{organizationId}/manage-properties/update-hotel-details/{hotelCode}',
};

@Injectable({ providedIn: 'root' })
export class PropertiesService {
    private http = inject(HttpGatewayService);

    public getProperties(organizationId: string): Observable<PropertiesResponseContract> {
        const url = new UrlBuilder(PROPERTY_URLS.GET_PROPERTIES).addOrganizationId(organizationId).toUrl();

        return this.http.get<PropertiesResponseContract>(url);
    }

    public configureMews(
        organizationId: string,
        hotelCode: string,
        interfaceType: string,
        accessToken: string,
    ): Observable<any> {
        const url = new UrlBuilder(PROPERTY_URLS.CONFIGURE)
            .addOrganizationId(organizationId)
            .addHotelCode(hotelCode)
            .toUrl();

        return this.http.put(url, {
            interfaceType,
            accessToken,
        });
    }

    public configureGuestline(
        organizationId: string,
        hotelCode: string,
        interfaceType: string,
        siteId: string,
        groupId: string,
    ): Observable<any> {
        const url = new UrlBuilder(PROPERTY_URLS.CONFIGURE)
            .addOrganizationId(organizationId)
            .addHotelCode(hotelCode)
            .toUrl();

        return this.http.put(url, {
            interfaceType,
            siteId,
            groupId,
        });
    }

    public configureShiji(
        organizationId: string,
        hotelCode: string,
        interfaceType: string,
        username: string,
        tenantName: string,
        password: string,
        propertyId: string,
    ): Observable<any> {
        const url = new UrlBuilder(PROPERTY_URLS.CONFIGURE)
            .addOrganizationId(organizationId)
            .addHotelCode(hotelCode)
            .toUrl();

        return this.http.put(url, {
            interfaceType,
            username,
            tenantName,
            password,
            propertyId,
        });
    }

    public configureSNT(
        organizationId: string,
        hotelCode: string,
        interfaceType: string,
        hotelId: number,
        clientId: string,
        clientSecret: string,
        baseUrl: string,
        tokenUrl: string,
    ): Observable<any> {
        const url = new UrlBuilder(PROPERTY_URLS.CONFIGURE)
            .addOrganizationId(organizationId)
            .addHotelCode(hotelCode)
            .toUrl();

        return this.http.put(url, {
            interfaceType,
            hotelId,
            clientId,
            clientSecret,
            baseUrl,
            tokenUrl,
        });
    }

    public configurePms(
        organization: Organization,
        property: Property,
        setup: PmsSetup,
        user: SignedInUser,
    ): Observable<any> {
        const data = this.buildPmsConfig(organization, property, setup, user);
        const url = new UrlBuilder(PROPERTY_URLS.CONFIGURE_PMS)
            .addOrganizationId(organization.id)
            .addHotelCode(property.code)
            .toUrl();

        return this.http.post(url, { data });
    }

    public addProperty(
        organizationId: string,
        name: string,
        capacity: number,
        cityData: CityData,
    ): Observable<{ id: number; code: string }> {
        const url = new UrlBuilder(PROPERTY_URLS.ADD_PROPERTY).addOrganizationId(organizationId).toUrl();

        return this.http.post(url, {
            name,
            capacity,
            city: cityData.city,
            countryCode: cityData.countryCode,
            currency: cityData.currencyCode,
            timezone: cityData.timezone,
        });
    }

    public disconnectProperty(organizationId: string, hotelCode: string): Observable<any> {
        const url = new UrlBuilder(PROPERTY_URLS.DISCONNECT_PROPERTY)
            .addOrganizationId(organizationId)
            .addHotelCode(hotelCode)
            .toUrl();

        return this.http.post(url, {});
    }

    public updateHotelCapacity(organizationId: string, hotelCode: string, capacity: number): Observable<any> {
        const url = new UrlBuilder(PROPERTY_URLS.UPDATE_HOTEL_CAPACITY)
            .addOrganizationId(organizationId)
            .addHotelCode(hotelCode)
            .toUrl();

        return this.http.post(url, { capacity });
    }

    public updateHotelDetails(
        organizationId: string,
        hotelCode: string,
        name: string,
        city: string,
        currency: string,
        countryCode: string,
        timezone: string,
    ): Observable<any> {
        const url = new UrlBuilder(PROPERTY_URLS.UPDATE_HOTEL_DETAILS)
            .addOrganizationId(organizationId)
            .addHotelCode(hotelCode)
            .toUrl();

        return this.http.post(url, { name, city, currency, countryCode, timezone });
    }

    private buildPmsConfig(
        organization: Organization,
        property: Property,
        setup: PmsSetup,
        user: SignedInUser,
    ): Record<string, string> {
        const config: Record<string, string> = {
            propertyName: property.name,
            propertyCode: property.code,
            propertyId: String(property.id),
            organizationName: organization?.name ?? '',
            organizationId: organization?.id ?? '',
            userName: `${user.firstName} ${user.lastName}`,
            userEmail: user.email,
        };

        for (const [key, value] of Object.entries(setup)) {
            config[key] = String(value);
        }

        return config;
    }
}
